import { Component, HostBinding, Input, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { DIALOG_ICON_WARNING, DIALOG_MSG_DO_YOU_WISH_CONTINUE, PromptConfirmDialogComponent, PromptConfirmInput } from "@merri/common";
import { BehaviorSubject, of } from "rxjs";
import { concatMap, tap } from "rxjs/operators";
import { LoggingService, PartnerFeatures, SessionService, SnackBarService } from "src/app/core";
import { CustomerRequestService } from "src/app/features/customer-requests/services";
import { VisionboardItem } from "../../../models";
import { VisionboardAttributesService, VisionboardService, VisionboardWorkspaceItemsStoreService } from "../../../services";

@Component({
    selector: "app-visionboard-item-more-actions-menu",
    templateUrl: "./visionboard-item-more-actions-menu.component.html",
    styleUrls: ["./visionboard-item-more-actions-menu.component.scss"],
    standalone: false
})
export class VisionboardItemMoreActionsMenuComponent implements OnInit {
  canDelete: boolean;

  @Input() item: VisionboardItem;
  @Input() source: string;
  @Input() isMenuOpen$: BehaviorSubject<boolean>;

  @ViewChild("moreActionsMenuTrigger", { read: MatMenuTrigger }) moreActionsMenuTrigger: MatMenuTrigger;
  @HostBinding("style.display") public display = "none";

  constructor(
    private _customerRequestService: CustomerRequestService,
    private _matDialog: MatDialog,
    private _sessionService: SessionService,
    private _snackBarService: SnackBarService,
    private _visionboardAttributesService: VisionboardAttributesService,
    private _visionboardService: VisionboardService,
    private _visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private _loggingService: LoggingService,
  ) {}

  ngOnInit(): void {
    if (this.item?.isCustomerUploadedSku) {
      this.display = "block";
    }

    this.canDelete = this._sessionService.isCurrentUserPartnerWithFeature(PartnerFeatures.CustomerVariantSkuManagement);
  }

  onMenuClose(): void {
    this.isMenuOpen$?.next(false);
  }

  onMoreActionsClick($event: MouseEvent): void {
    $event.stopPropagation();

    this.isMenuOpen$?.next(true);

    this._loggingService.logRemoteInfo("Visionboard Item More Actions", "VisionboardItemMoreActionsMenuComponent.onMoreActionsClick", {
      Workflow: "VisionBoard Interaction",
      WorkflowStep: "More Actions Menu Opened",
      WorkflowSource: this.source,
      VariantSkuId: `${this.item.variantSkuId}`,
    });

    this.moreActionsMenuTrigger.openMenu();
  }

  onEditClick(): void {
    this._loggingService.logRemoteInfo("Visionboard Item More Actions", "VisionboardItemMoreActionsMenuComponent.onEditClick", {
      Workflow: "VisionBoard Interaction",
      WorkflowStep: "More Actions Edit Click",
      WorkflowSource: this.source,
      VariantSkuId: `${this.item.variantSkuId}`,
    });

    this._visionboardService.showItemRequestDialog(
      this._matDialog,
      `Edit Uploaded Item - VB Item Hover Menu from ${this.source ?? "UNKNOWN"}`,
      this.item.variantSkuId,
    );
  }

  onDeleteClick(): void {
    this._loggingService.logRemoteInfo("Visionboard Item More Actions", "VisionboardItemMoreActionsMenuComponent.onDeleteClick", {
      Workflow: "VisionBoard Interaction",
      WorkflowStep: "More Actions Delete Click",
      WorkflowSource: this.source,
      VariantSkuId: `${this.item.variantSkuId}`,
    });

    PromptConfirmDialogComponent.promptYesConfirm$(this._matDialog, {
      title: "Delete Item",
      header: DIALOG_MSG_DO_YOU_WISH_CONTINUE,
      message: `Deleting the custom item <strong>${this.item.skuName}</strong> will remove it from your Vision Board and 3D Design!`,
      icon: DIALOG_ICON_WARNING,
    } as PromptConfirmInput)
      .pipe(
        concatMap(accept => {
          if (accept) {
            return this._customerRequestService.submitDeleteSku$(this.item.variantSkuId).pipe(
              tap(success => {
                this._snackBarService.show({
                  message: success ? "Your item has been removed." : "Failed to remove item, try again.",
                  action: "Hide",
                  duration: 6000,
                });

                if (success) {
                  this._visionboardWorkspaceItemsStoreService.removeItems(this.item.variantSkuId);
                  this._visionboardAttributesService.raiseFiltersChanged();
                }
              }),
            );
          }

          return of(false);
        }),
      )
      .subscribe();
  }
}
